import { useExtensionStore } from './store';
import { validateManifest } from './registry';
import { compiledInExtensions, compiledInExtensionIds } from './bundledIds';
import { listPublished } from './marketplaceClient';
import { marketplaceEnabled } from '../utils/featureFlags';
import { isTauri } from '../utils/tauri';
import { onStorageNamespaceChange } from '../services/storageNamespace';

/**
 * Registers installed extensions into useExtensionStore once, on first
 * import. Imported for its side effect (e.g. `import './extensions/bootstrap'`
 * near App.js's top-level imports) so extensions are registered before
 * ActivityBar/Sidebar first render.
 *
 * Delivery model (Phase 6): first-party extensions ship BUNDLED into the app
 * binary and are registered unconditionally from the curated list in
 * bundled/index.js — trusted, so their manifest-declared capabilities are
 * auto-granted with no consent screen (deliberate, documented consent-model
 * change; see bundled/index.js). Persisted user state always wins over those
 * defaults: a disable or permission-revoke made in Settings > Extensions
 * survives every launch. Bundled extensions are ENABLE/DISABLE ONLY — they
 * ship inside the binary, so there is nothing an "uninstall" could remove and
 * Settings does not offer one; only third-party marketplace extensions can be
 * uninstalled (dismissed). The marketplace is the future
 * third-party path and is feature-gated OFF by default
 * (`marketplaceEnabled()` in utils/featureFlags.js) — its code stays intact
 * and compiling. Startup:
 *  0. Two one-shot migrations, both for state whose UI no longer exists, and
 *     both running first because they rewrite state steps 1 and 2 read:
 *       - store.undismissBundled — legacy dismissals on bundled ids, left by
 *         an Uninstall button that only hid the extension and which nothing
 *         else can clear now that it is gone.
 *       - store.migrateAllOrNothingRevokes — per-capability revokes on
 *         extensions that no longer offer a per-capability toggle, which
 *         reconcileBundledGrants would otherwise honour forever.
 *  1. Offline-first registration of every marketplace-installed extension
 *     from its cached bundle on disk — never waits on the network. Bundled
 *     ids are skipped here: the compiled-in source shadows a stale cached
 *     marketplace install of the same id.
 *  2. Unconditional registration of the curated bundled list (production
 *     path), plus — dev convenience only, with
 *     REACT_APP_BUNDLED_EXTENSIONS=true — the dev-only sources in
 *     dev/localExtensions.js (empty since 03.08.2026).
 *  3. Only when the marketplace flag is on: a best-effort async marketplace
 *     sync afterward — newer published versions of *installed* extensions
 *     are downloaded + verified + swapped in. Any failure is caught and
 *     logged; whatever's already registered keeps running untouched.
 */
let bootstrapped = false;

// Every id whose source is compiled into this build (bundled + the dev-flagged
// local extensions) now lives in ./bundledIds — the same definition Settings
// uses to decide that "Uninstall" must not be offered. These win over a cached
// marketplace install of the same id: otherwise a stale cached bundle would
// re-register on each launch and permanently shadow the shipped code (the
// background sync, when enabled at all, only replaces it when the *published*
// version bumps).
const compiledInIds = compiledInExtensionIds;

async function readCachedEntrySource(bundlePath) {
  const fs = await import('@tauri-apps/plugin-fs');
  return fs.readTextFile(bundlePath);
}

/**
 * Grant reconciliation for BUNDLED (first-party, auto-granted) extensions.
 *
 * The persisted grant array used to fully replace the manifest auto-grant, so
 * a profile that ran DB Manager while its manifest was MySQL-only kept
 * `['db:mysql','secureStorage']` forever — `db:postgres`/`db:sqlite` added by
 * an app update were never granted ("Extension missing granted capability"
 * at every postgres connect).
 *
 * Strategy: granted = persisted ∪ (manifestCaps − lastSeenManifestSnapshot),
 * minus anything in the persisted `revokedCapabilities` ledger. The snapshot
 * (`knownManifestCaps`, written after every registration) makes "newly
 * declared by an update" precise going forward; explicit revokes are recorded
 * by Settings' toggle (store.setCapabilityGranted) so they survive the union.
 *
 * Migration trade-off (documented, deliberate): profiles that predate the
 * snapshot can't distinguish "revoked before tracking existed" from "stale".
 * A permission system must resolve that ambiguity toward consent, not
 * availability — so on the FIRST snapshot-aware launch the persisted grant
 * set wins verbatim (missing manifest caps are treated as implicit revokes
 * and NOT re-granted). The union only heals grant sets against manifest
 * additions that land after the snapshot baseline exists. Cost: a
 * pre-tracking profile whose update added new engines before any snapshot
 * must enable them once in Settings. Third-party marketplace extensions are
 * NOT reconciled this way: new capabilities in an updated third-party
 * manifest must go through an explicit consent surface, not a silent union.
 */
function reconcileBundledGrants(extensionId, manifestCaps) {
  const state = useExtensionStore.getState();
  const persisted = state.grantedCapabilities[extensionId];
  if (!persisted) return manifestCaps; // fresh profile — plain auto-grant
  const revoked = new Set(state.revokedCapabilities?.[extensionId] || []);
  const snapshotRaw = state.knownManifestCaps?.[extensionId];
  if (!Array.isArray(snapshotRaw)) {
    // No snapshot baseline yet (first launch of a snapshot-aware build over
    // an existing profile): the persisted grants are authoritative. Never
    // union manifest caps in here — a cap the user revoked before the ledger
    // existed would be silently re-granted (keychain/DB access without any
    // consent surface). noteManifestCapsSeen seeds the baseline right after
    // registration, so future manifest additions still auto-union.
    return persisted.filter((cap) => !revoked.has(cap));
  }
  const snapshot = new Set(snapshotRaw);
  const granted = new Set(persisted);
  for (const cap of manifestCaps) {
    if (!snapshot.has(cap) && !revoked.has(cap)) granted.add(cap);
  }
  // Belt and braces: a cap can't legitimately be both granted and revoked
  // (setCapabilityGranted keeps the two in sync), but never let one through.
  return [...granted].filter((cap) => !revoked.has(cap));
}

export function bootstrapExtensions() {
  if (bootstrapped) return;
  bootstrapped = true;

  // MIGRATION, before anything reads the persisted slice. Bundled extensions
  // used to offer an Uninstall button that only set a dismissal flag (the code
  // ships in the binary and was never removed). That surface is gone, so any
  // dismissal still sitting on a bundled id would strand it invisible forever
  // with nothing able to clear it. Translate those into "disabled" instead —
  // the honest equivalent of what the dismissal actually did — so the
  // extension is present-but-off and one switch away from coming back.
  // Ordering is load-bearing: this rewrites `dismissedExtensions`/`enabled`,
  // both of which are read below.
  useExtensionStore.getState().undismissBundled([...compiledInIds]);

  // MIGRATION 2, same reasoning, different stranded state. Settings no longer
  // offers a toggle per capability for extensions whose capabilities are
  // all-or-nothing, so a revoke persisted by that old UI can no longer be
  // cleared by anything — and `reconcileBundledGrants` below would keep
  // withholding the capability forever, leaving a half-working extension with
  // no control that could repair it. This translates such a revoke into
  // "present but disabled" and drops the dead ledger entry; the capabilities
  // are re-granted only when the user turns the switch back on (store.setEnabled).
  // Ordering is load-bearing twice over: it rewrites `enabled`, read just
  // below, and `revokedCapabilities`, read by reconcileBundledGrants.
  useExtensionStore
    .getState()
    .migrateAllOrNothingRevokes(compiledInExtensions.map((ext) => ext.manifest));

  const { installedFromMarketplace, dismissedExtensions } = useExtensionStore.getState();

  // Persisted (rehydrated) per-extension state from previous sessions — a
  // user's disable or permission-revoke must win over install-time defaults
  // on every re-registration, or toggling an extension off would silently
  // undo itself each launch.
  const persistedEnabled = useExtensionStore.getState().enabled;
  const persistedGrants = useExtensionStore.getState().grantedCapabilities;

  // 1. Every marketplace install, registered from its cached bundle on disk
  //    (offline-safe). This is keyed purely off the install records — an
  //    extension does not need to be known to this build to survive restart.
  for (const [slug, record] of Object.entries(installedFromMarketplace)) {
    if (dismissedExtensions[slug]) continue;
    if (compiledInIds.has(slug)) continue; // compiled-in source shadows a stale cached install
    if (!record?.bundlePath || !record?.manifest || !isTauri()) continue;
    const { valid, errors } = validateManifest(record.manifest);
    if (!valid) {
      console.warn(`[extensions] Cached manifest for "${slug}" failed validation, skipping:`, errors);
      continue;
    }
    if (record.manifest.id !== slug) {
      console.warn(`[extensions] Cached manifest id "${record.manifest.id}" does not match install slug "${slug}", skipping.`);
      continue;
    }
    readCachedEntrySource(record.bundlePath)
      .then((entrySource) => {
        useExtensionStore.getState().registerExtension(record.manifest, {
          enabled: persistedEnabled[slug] ?? true,
          // No fallback to the install record: that record is device-global,
          // so falling back to it would hand the installing account's grants
          // to every other account on the machine. An account with no
          // persisted grants for a marketplace extension gets NONE and makes
          // its own consent decision in Settings > Extensions.
          grantedCapabilities: persistedGrants[slug] ?? [],
          entrySource,
        });
      })
      .catch((err) => {
        console.warn(`[extensions] Cached bundle unreadable for "${slug}" — reinstall it from Settings > Extensions:`, err);
      });
  }

  // 2. The curated first-party bundled list (production path, always on),
  //    plus — dev flag only — the dev-only extensions. Both register through
  //    the identical contract; capabilities default to the manifest-declared
  //    auto-grant, but a user's persisted disable/revoke wins. Registration is
  //    unconditional: these always ship, so the list here is the full set the
  //    user can toggle on and off — never a subset they could whittle away.
  for (const { manifest, entrySource, grantedCapabilities } of compiledInExtensions) {
    // Defensive only: `undismissBundled` above just cleared every dismissal on
    // a compiled-in id, and unregisterExtension refuses to set one. Kept so a
    // hand-edited localStorage blob still can't crash the loop.
    if (dismissedExtensions[manifest.id]) continue;
    const { valid, errors } = validateManifest(manifest);
    if (!valid) {
      console.error(`[extensions] Invalid manifest for "${manifest?.id}":`, errors);
      continue;
    }
    useExtensionStore.getState().registerExtension(manifest, {
      enabled: persistedEnabled[manifest.id] ?? true,
      // Not `persisted ?? manifest`: stale persisted grants must be
      // reconciled against capabilities the manifest declared after they
      // were written — see reconcileBundledGrants.
      grantedCapabilities: reconcileBundledGrants(manifest.id, grantedCapabilities),
      entrySource,
    });
    // Snapshot the manifest caps just registered — the baseline the next
    // launch diffs against to detect newly-declared capabilities.
    useExtensionStore.getState().noteManifestCapsSeen(manifest.id, grantedCapabilities);
  }

  // 3. Marketplace update sync — only when the marketplace surface is
  //    enabled at all (default OFF since Phase 6; bundled is the delivery
  //    path). The sync code itself stays intact for the future third-party
  //    distribution path.
  if (marketplaceEnabled()) {
    syncMarketplace().catch((err) => {
      console.warn('[extensions] Background marketplace sync failed (offline?):', err);
    });
  }
}

/**
 * Best-effort background check: for every published extension whose latest
 * version is newer than what's currently registered, download + verify +
 * swap in. Never throws past its own boundary — callers treat any rejection
 * as "no update this time."
 */
async function syncMarketplace() {
  if (!isTauri()) return; // no download command available outside Tauri

  const published = await listPublished();
  if (!Array.isArray(published) || published.length === 0) return;

  const { invoke } = await import('@tauri-apps/api/core');

  for (const entry of published) {
    const latest = entry.latest_version;
    if (!latest) continue;

    const slug = entry.slug;
    const state = useExtensionStore.getState();
    if (state.dismissedExtensions[slug]) continue; // user uninstalled — don't silently reinstall via background sync
    if (compiledInIds.has(slug)) continue; // never swap compiled-in source for a published bundle

    const currentManifest = state.extensions[slug];
    const cachedRecord = state.installedFromMarketplace[slug];
    const currentVersion = cachedRecord?.version || currentManifest?.version;

    // Background sync is UPDATE-only. A slug with no current version was
    // never installed here — installing it anyway would silently add code
    // (and a consent-free permission surface) the user never asked for.
    // First-time installs go through the Settings > Extensions consent flow.
    if (!currentVersion) continue;
    if (!isNewer(latest.version, currentVersion)) continue;

    try {
      const { manifest: newManifest, errors: validationErrors, valid } = validateFetchedManifest(latest.manifest);
      if (!valid) {
        console.warn(`[extensions] Marketplace manifest for "${slug}" failed validation:`, validationErrors);
        continue;
      }
      // The manifest id is the registry key for entrySource + granted
      // capabilities. If it doesn't match the marketplace slug, a published
      // entry could overwrite a *different* extension's registration (code
      // swap under an already-trusted identity) — reject the mismatch.
      if (newManifest.id !== slug) {
        console.warn(`[extensions] Marketplace manifest id "${newManifest.id}" does not match slug "${slug}" — skipping.`);
        continue;
      }

      const download = await invoke('download_extension_bundle', {
        request: {
          url: latest.bundle_url,
          slug,
          version: latest.version,
          sha256: latest.sha256,
        },
      });

      const entrySource = await readCachedEntrySource(download.path);
      // This account's live (persisted) grants are the ONLY source — a version
      // swap is not a consent event, and the device-global install record
      // carries no grants to fall back to.
      const grantedCapabilities = state.grantedCapabilities[slug] ?? [];

      useExtensionStore.getState().registerExtension(newManifest, {
        enabled: state.enabled[slug] !== false,
        grantedCapabilities,
        entrySource,
      });
      useExtensionStore.getState().recordMarketplaceInstall(slug, {
        manifest: newManifest,
        version: latest.version,
        bundlePath: download.path,
      });
    } catch (err) {
      console.warn(`[extensions] Failed to sync marketplace update for "${slug}":`, err);
    }
  }
}

/**
 * Re-run registration for the account that just signed in (or out).
 *
 * The store's persisted consent slice is re-read from the incoming account's
 * key by rehydrateOnAccountChange, which leaves every already-registered
 * extension holding the OUTGOING account's grants in memory — or, for an
 * account that has never used this device, no grants at all. Neither is
 * correct, so registration runs again against the fresh slice: persisted
 * disables/revokes/uninstalls for THIS account win, and anything this account
 * has never decided falls back to the same defaults a first launch would give
 * it. Cheap (the bundled sources are already in memory) and idempotent.
 */
export function rebootstrapExtensionsForAccount() {
  bootstrapped = false;
  bootstrapExtensions();
}

onStorageNamespaceChange(rebootstrapExtensionsForAccount);

function validateFetchedManifest(manifest) {
  const { valid, errors } = validateManifest(manifest);
  return { manifest, valid, errors };
}

function isNewer(candidate, current) {
  const c = String(candidate).split('.').map((n) => parseInt(n, 10) || 0);
  const cur = String(current).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(c.length, cur.length); i++) {
    const a = c[i] || 0;
    const b = cur[i] || 0;
    if (a !== b) return a > b;
  }
  return false;
}

bootstrapExtensions();
